/** Pure checks for whether a decision answer is ready to confirm/submit. */

export type DecisionAnswerFields = {
  sliderTouched: boolean;
  confidenceTouched: boolean;
  confidence: number | null;
  justification: string;
  maxChars: number;
};

export type MissingDecisionPart = "percent" | "justification" | "confidence";

export type DecisionFormSeed = {
  share: number | null;
  touched: boolean;
  confidence: number | null;
  confidenceTouched: boolean;
  justification: string;
  revision: number;
};

type DecisionSeedInput = {
  show_previous?: boolean;
  previous?: { share_a: number | null } | null;
  draft?: {
    share_a: number | null;
    slider_touched: boolean;
    confidence: number | null;
    confidence_touched: boolean;
    justification: string;
    revision: number;
  } | null;
} | null;

const blankDecisionSeed = (): DecisionFormSeed => ({
  share: null,
  touched: false,
  confidence: null,
  confidenceTouched: false,
  justification: "",
  revision: 1,
});

/** D1 stays empty. D2 and D3 open on the previous share. A draft for this decision wins. */
export function seedForDecision(decision: DecisionSeedInput): DecisionFormSeed {
  if (!decision) return blankDecisionSeed();
  if (decision.draft) {
    return {
      share: decision.draft.share_a,
      touched: decision.draft.slider_touched,
      confidence: decision.draft.confidence,
      confidenceTouched: decision.draft.confidence_touched,
      justification: decision.draft.justification,
      revision: decision.draft.revision,
    };
  }
  const previousShare =
    decision.show_previous && decision.previous?.share_a != null ? decision.previous.share_a : null;
  if (previousShare == null) return blankDecisionSeed();
  return {
    share: previousShare,
    touched: true,
    confidence: null,
    confidenceTouched: false,
    justification: "",
    revision: 1,
  };
}

export function missingDecisionParts(fields: DecisionAnswerFields): MissingDecisionPart[] {
  const missing: MissingDecisionPart[] = [];
  if (!fields.sliderTouched) missing.push("percent");
  // Justification is optional (effective min length is always 0); only max is enforced.
  if (fields.justification.length > fields.maxChars) {
    missing.push("justification");
  }
  if (!fields.confidenceTouched || fields.confidence == null) missing.push("confidence");
  return missing;
}

export function isDecisionComplete(fields: DecisionAnswerFields): boolean {
  return missingDecisionParts(fields).length === 0;
}

/** Short, click-time hint listing what still needs to be filled in. */
export function formatMissingDecisionHint(
  fields: DecisionAnswerFields,
  missing: MissingDecisionPart[] = missingDecisionParts(fields),
): string {
  if (missing.length === 0) return "";
  const labels = missing.map((part) => {
    if (part === "percent") return "percent";
    if (part === "confidence") return "confidence";
    return `justification (at most ${fields.maxChars} characters)`;
  });
  if (labels.length === 1) return `Please set ${labels[0]}.`;
  if (labels.length === 2) return `Please set ${labels[0]} and ${labels[1]}.`;
  return `Please set ${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}.`;
}
